/**
 * The editor-facing projection of the AGENTIC LOGIC INTEGRATION CLIENTS — the
 * per-org third-party clients the platform calls on behalf of user-authored
 * code (Agentic Logic rules, custom shipping providers). Mirrors the zod contract
 * in ig-types `functions/agentic-logic/integration-clients/catalog.ts`, which stays the
 * source of truth; this is the plain-TypeScript text spliced into each
 * domain's injected `./types` module (`SHIPPING_PROVIDER_TYPES_MODULE_SOURCE`,
 * `buildAgenticLogicTypesModuleSource`).
 *
 * Hand-authored text, like every other snippet in this package, kept honest
 * two ways: the type-level assertions at the bottom pin each declared
 * request and response to its ig-types type, and node-backend's
 * `agentic-logic-module-sources.spec.ts` pins every catalog key and operation
 * to a rendered method. Adding a provider = one catalog entry in ig-types +
 * one client block here (+ its assertion).
 */
import type {
  AgenticLogicIntegrationClientKey,
  UpsRateRequest,
  UpsRateResponse,
} from "@intelligems/ig-types/functions/agentic-logic/integration-clients/catalog";

/**
 * The `AgenticLogicIntegrationClients` type and the per-client request/response shapes,
 * as module source. Every domain that hands user code `clients` appends this
 * fragment to its types module and references `AgenticLogicIntegrationClients` from its
 * own input type.
 */
export const AGENTIC_LOGIC_INTEGRATION_CLIENTS_TYPES_SOURCE = `// ----- integration clients -----
// Per-org third-party clients the platform calls FOR your code. Your code
// still has no network access: each call crosses to the trusted side, which
// resolves the org's credential and calls the carrier. Every call is one SDK
// read — it is never cached, seeded or prefetched, and the answer comes from
// outside the platform, so keep the envelope stable across calls. A client
// whose integration the org has not connected THROWS a denial
// (\`integration_client_unavailable\`); a carrier failure throws like any other
// failed read.

/**
 * A UPS Rating API request. \`RateRequest\` is UPS's own body, passed through;
 * the platform fills \`RateRequest.Shipment.Shipper.ShipperNumber\` from the
 * org's account number when it is omitted.
 */
export type UpsRateRequest = { RateRequest: Record<string, unknown> };

/** UPS's Rating response, the JSON object UPS returned, verbatim. */
export type UpsRateResponse = Record<string, unknown>;

export type AgenticLogicIntegrationClients = {
  ups: {
    /**
     * One UPS Rating call: one SDK read, never cached or seeded. Throws a
     * denial (\`integration_client_unavailable\`) when the org has not
     * connected UPS.
     */
    rate(args: UpsRateRequest): Promise<UpsRateResponse>;
  };
};
`;

// ---------------------------------------------------------------------------
// Type-level guard: the text above restated as types, pinned to ig-types.
// The text is what ships; these assertions are what fail the build when the
// catalog and the text drift (a renamed field, a widened response, a client
// the text forgot).
// ---------------------------------------------------------------------------

/** The shape `AGENTIC_LOGIC_INTEGRATION_CLIENTS_TYPES_SOURCE` declares, as a type. */
type DeclaredAgenticLogicIntegrationClients = {
  ups: {
    rate(args: {
      RateRequest: Record<string, unknown>;
    }): Promise<Record<string, unknown>>;
  };
};

type MutuallyAssignable<A, B> = [A] extends [B]
  ? [B] extends [A]
    ? true
    : never
  : never;

/** Every catalog client is declared, and nothing else is. */
const _declaresEveryClient: MutuallyAssignable<
  keyof DeclaredAgenticLogicIntegrationClients,
  AgenticLogicIntegrationClientKey
> = true;

/** `ups.rate` takes exactly the ig-types request and resolves exactly its response. */
const _upsRateRequestMatches: MutuallyAssignable<
  Parameters<DeclaredAgenticLogicIntegrationClients["ups"]["rate"]>[0],
  UpsRateRequest
> = true;

const _upsRateResponseMatches: MutuallyAssignable<
  Awaited<ReturnType<DeclaredAgenticLogicIntegrationClients["ups"]["rate"]>>,
  UpsRateResponse
> = true;

void _declaresEveryClient;
void _upsRateRequestMatches;
void _upsRateResponseMatches;
